import React, { useEffect, useState } from "react";
import {
  Grid,
  Paper,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  useMediaQuery,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  FormControlLabel,
  Switch,
  Drawer,
  TextField,
  Box,
  IconButton,
  Checkbox,
  CircularProgress,
  Alert,
} from "@mui/material";
import { ArrowDropDown, Delete, Edit, Settings, Close } from "@mui/icons-material";
import Search from "../../../components/common/search/Search";
import Input from "../../../components/common/input/Input";
import axiosInstance from "../../../utils/axios/axiosInstance";
import API from "../../../config/apiconfig";
import { useDispatch } from "react-redux";
import { userApiLoader } from "../../../action/loaderActions";
import { toast } from "react-toastify";

const ClassSchedulesTab = () => {
  const dispatch = useDispatch();
  const [data, setData] = useState([]);
  const [universitySource, setUniversitySource] = useState("");
  const [courseName, setCourseName] = useState("");
  const [className, setClassName] = useState("");
  const [professor, setProfessor] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [location, setLocation] = useState("");
  const [reminder, setReminder] = useState(false);
  const [syncGoogleCalendar, setSyncGoogleCalendar] = useState(false);
  const isLgScreen = useMediaQuery((theme) => theme.breakpoints.up("lg"));
  const [settings, setSettings] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState("");
  const [sortedData, setSortedData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Helper function to format date for display
  const formatDisplayDate = (dateValue) => {
    if (!dateValue) return "N/A";
    
    // If it's already a valid date string (YYYY-MM-DD), return as is
    if (typeof dateValue === 'string' && dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateValue;
    }
    
    // If it's a number or invalid format, try to convert
    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) {
        return "Invalid Date";
      }
      return date.toISOString().split('T')[0];
    } catch (error) {
      return "Invalid Date"; 
    }
  };

  // Helper function to validate and format date
  const validateAndFormatDate = (dateValue) => {
    if (!dateValue) return getCurrentDate();
    
    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) {
        return getCurrentDate();
      }
      return date.toISOString().split('T')[0];
    } catch (error) {
      return getCurrentDate();
    }
  };

  const handleSort = (column) => {
    setSelectedFilter(column);
    if (!column) {
      setSortedData(data);
      return;
    }
    const sorted = [...sortedData].sort((a, b) => {
      if (a[column] < b[column]) return -1;
      if (a[column] > b[column]) return 1;
      return 0;
    });
    setSortedData(sorted);
  };

  const handleSearch = (value) => {
    setSearchQuery(value);
    const filteredData = data.filter((row) =>
      Object.values(row).some((val) => {
        if (val === null || val === undefined) return false;
        return val.toString().toLowerCase().includes(value.toLowerCase());
      })
    );
    setSortedData(filteredData);
  };

  const getClassesList = () => {
    dispatch(userApiLoader({ isApiLoader: true }));
    axiosInstance
      .get(`${API.USER_MANAGEMENT_API_BASE_URL}${API.TOOLS}${API.GET_CLASSES}`)
      .then((res) => {
        const list = res?.data?.data || [];
        
        // Clean and format the data, especially dates
        const cleanedList = list.map(item => ({
          ...item,
          date: formatDisplayDate(item.date),
          // Ensure other fields are properly formatted
          class_name: item.class_name || "",
          professor: item.professor || "",
          time: item.time || "",
          end_time: item.end_time || "",
          location: item.location || "",
          student_id: item.student_id || "",
        }));
        
        setData(cleanedList);
        setSortedData(cleanedList);
        dispatch(userApiLoader({ isApiLoader: false }));
        toast.success(res?.data?.message, { autoClose: 10000 });
      })
      .catch((err) => {
        dispatch(userApiLoader({ isApiLoader: false }));
        toast.error(err?.response?.data?.message || "Failed to fetch", {
          autoClose: 10000,
        });
      });
  };

  useEffect(() => {
    getClassesList();
    // Initialize form with current date and time
    const currentTime = getCurrentTime();
    setDate(getCurrentDate());
    setTime(currentTime);
    setEndTime(addMinutesToTime(currentTime, 30));
  }, []);

  const createNewClass = () => {
    setSubmitting(true);
    dispatch(userApiLoader({ isApiLoader: true }));

    const formatTimeToAmPm = (timeStr) => {
      const [hour, minute] = timeStr.split(":").map(Number);
      const ampm = hour >= 12 ? "PM" : "AM";
      const formattedHour = hour % 12 || 12;
      return `${formattedHour}:${minute.toString().padStart(2, "0")} ${ampm}`;
    };

    const formattedTime = formatTimeToAmPm(time);
    const formattedEndTime = formatTimeToAmPm(endTime || time);

    const payload = {
      class_name: className,
      professor: professor,
      date: validateAndFormatDate(date), // Ensure date is properly formatted
      time: formattedTime,
      end_time: formattedEndTime,
      location: location,
      reminder: reminder,
      sync_google_calendar: syncGoogleCalendar ? 1 : 0,
      google_calendar_token: "",
      university_source: universitySource || "Default University",
      course_name: courseName,
    };

    axiosInstance
      .post(
        `${API.USER_MANAGEMENT_API_BASE_URL}${API.TOOLS}${API.GET_CLASSES}`,
        payload
      )
      .then((res) => {
        dispatch(userApiLoader({ isApiLoader: false }));
        toast.success(res?.data?.message, { autoClose: 6000 });
        getClassesList();
        resetForm();
        setSettings(false);
      })
      .catch((err) => {
        dispatch(userApiLoader({ isApiLoader: false }));
        toast.error(err?.response?.data?.message || "Creation failed", {
          autoClose: 6000,
        });
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const handleEdit = (classData) => {
    setIsEditing(true);
    setEditingId(classData.id);
    setSettings(true);
    setClassName(classData.class_name || "");
    setProfessor(classData.professor || "");
    setDate(validateAndFormatDate(classData.date)); // Validate and format date

    const convertTo24Hour = (time12h) => {
      if (!time12h) return "";
      const [timePart, modifier] = time12h.split(" ");
      let [hours, minutes] = timePart.split(":");
      if (modifier === "PM" && hours !== "12") {
        hours = String(parseInt(hours, 10) + 12);
      }
      if (modifier === "AM" && hours === "12") {
        hours = "00";
      }
      return `${hours.toString().padStart(2, "0")}:${minutes}`;
    };

    setTime(convertTo24Hour(classData.time || ""));
    setEndTime(convertTo24Hour(classData.end_time || ""));
    setLocation(classData.location || "");
    setReminder(classData.reminder === 1);
    setSyncGoogleCalendar(classData.sync_google_calendar === 1);
    setUniversitySource(classData.university_source || "");
    setCourseName(classData.course_name || "");
  };

  // Helper function to get current date in YYYY-MM-DD format
  const getCurrentDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  // Helper function to get current time in HH:MM format
  const getCurrentTime = () => {
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // Helper function to add 30 minutes to a time string
  const addMinutesToTime = (timeString, minutesToAdd = 30) => {
    if (!timeString) return '';
    
    const [hours, minutes] = timeString.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes + minutesToAdd, 0, 0);
    
    const newHours = date.getHours().toString().padStart(2, '0');
    const newMinutes = date.getMinutes().toString().padStart(2, '0');
    
    return `${newHours}:${newMinutes}`;
  };

  const resetForm = () => {
    setUniversitySource("Default University");
    setCourseName("");
    setClassName("");
    setProfessor("");
    setDate(getCurrentDate());
    const currentTime = getCurrentTime();
    setTime(currentTime);
    setEndTime(addMinutesToTime(currentTime, 30));
    setLocation("");
    setReminder(false);
    setSyncGoogleCalendar(false);
    setEditingId(null);
    setIsEditing(false);
  };

  const updateClass = () => {
    setSubmitting(true);
    dispatch(userApiLoader({ isApiLoader: true }));

    const formatTimeToAmPm = (timeStr) => {
      const [hour, minute] = timeStr.split(":").map(Number);
      const ampm = hour >= 12 ? "PM" : "AM";
      const formattedHour = hour % 12 || 12;
      return `${formattedHour}:${minute.toString().padStart(2, "0")} ${ampm}`;
    };

    const formattedTime = formatTimeToAmPm(time);
    const formattedEndTime = formatTimeToAmPm(endTime || time);

    const payload = {
      id: editingId,
      class_name: className,
      professor: professor,
      date: validateAndFormatDate(date), // Ensure date is properly formatted
      time: formattedTime,
      end_time: formattedEndTime,
      location: location,
      reminder: reminder,
      sync_google_calendar: syncGoogleCalendar ? 1 : 0,
      google_calendar_token: "",
      university_source: universitySource || "Default University",
      course_name: courseName,
    };

    axiosInstance
      .put(
        `${API.USER_MANAGEMENT_API_BASE_URL}${API.TOOLS}${API.GET_CLASSES}`,
        payload
      )
      .then((res) => {
        dispatch(userApiLoader({ isApiLoader: false }));
        toast.success(res?.data?.message, { autoClose: 6000 });
        getClassesList();
        resetForm();
        setSettings(false);
      })
      .catch((err) => {
        dispatch(userApiLoader({ isApiLoader: false }));
        toast.error(err?.response?.data?.message || "Update failed", {
          autoClose: 6000,
        });
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const deleteClass = (row) => {
    dispatch(userApiLoader({ isApiLoader: true }));
    axiosInstance
      .delete(
        `${API.USER_MANAGEMENT_API_BASE_URL}${API.TOOLS}${API.GET_CLASSES}/${row.id}`
      )
      .then((res) => {
        dispatch(userApiLoader({ isApiLoader: false }));
        toast.success(res?.data?.message, { autoClose: 6000 });
        getClassesList();
      })
      .catch((err) => {
        dispatch(userApiLoader({ isApiLoader: false }));
        toast.error(err?.response?.data?.message || "Delete failed", {
          autoClose: 6000,
        });
      });
  };

  const closeModal = () => {
    setSettings(false);
    resetForm();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isEditing) {
      updateClass();
    } else {
      createNewClass();
    }
  };

  return (
    <Grid
      container
      spacing={2}
      style={{ padding: "0px !important" }}
    >

      <div className="container">
        <div class="css-11ezll3-MuiToolbar-root breadcrumbs">
          <div className="top-sec">
            <div className="card custom-card">
              <div class="card-body ">


                <div className="d-flex align-items-center justify-content-between flex-wrap pb-1">
                  <div class="d-flex align-items-center">
                    <h3 class="mb-2">Class Schedules</h3>
                  </div>
                  <div class="d-flex align-items-center flex-wrap mb-1">
                    <Button variant="contained" color="primary" className="!w-fit !px-3 !h-10"
                      onClick={() => {
                        setIsEditing(false);
                        setEditingId(null);
                        resetForm();
                        setSettings(true);
                      }}>
                      + Add New Class
                    </Button>
                  </div>
                </div>


                <div className="row filter-panel mt-4">
                  <div className="col-md-4">
                    <div className="serarch-bar">
                      <Search
                        value={searchQuery}
                        handleInputChange={(e) => handleSearch(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-md-6"></div>
                  <div className="col-md-2">
                    <div className="w-full flex items-center justify-end gap-2">
                      <FormControl fullWidth className="filter">
                        <InputLabel>
                          Filter
                        </InputLabel>
                        <Select
                          value={selectedFilter}
                          onChange={(e) => handleSort(e.target.value)}
                          label="Filter"
                          IconComponent={(props) => (
                            <ArrowDropDown {...props} style={{ color: "#c942b1" }} />
                          )}
                          style={{ height: 40 }}
                        >
                          <MenuItem value="">None</MenuItem>
                          <MenuItem value="student_id">Student ID</MenuItem>
                          <MenuItem value="university">University</MenuItem>
                          <MenuItem value="course_name">Course Name</MenuItem>
                          <MenuItem value="date">Date</MenuItem>
                        </Select>
                      </FormControl>
                    </div>
                  </div>
                </div>


                <div className="row">
                  <div className="col-md-12">
                    <div class="table-responsive mb-0">
                  
                        <TableContainer  className="table table-hover table-bordered mb-0 text-md-nowrap text-lg-nowrap text-xl-nowrap table-striped">
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell>Student ID</TableCell>
                                <TableCell>Class Name</TableCell>
                                <TableCell>Date</TableCell>
                                <TableCell>Start Time</TableCell>
                                <TableCell>End Time</TableCell>
                                <TableCell>Location</TableCell>
                                <TableCell>Professor</TableCell>
                                <TableCell align="center">Action</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {sortedData.length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={8} align="center">
                                    <Typography variant="body2" color="textSecondary">
                                      No class schedules found
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ) : (
                                sortedData.map((row, index) => (
                                  <TableRow key={index}>
                                    <TableCell>{row.student_id || "N/A"}</TableCell>
                                    <TableCell>{row.class_name || "N/A"}</TableCell>
                                    <TableCell>
                                      <Typography 
                                        variant="body2" 
                                        sx={{ 
                                          color: row.date === "Invalid Date" ? "error.main" : "inherit",
                                          fontWeight: row.date === "Invalid Date" ? "bold" : "normal"
                                        }}
                                      >
                                        {formatDisplayDate(row.date)}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>{row.time || "N/A"}</TableCell>
                                    <TableCell>{row.end_time || "N/A"}</TableCell>
                                    <TableCell>{row.location || "N/A"}</TableCell>
                                    <TableCell>{row.professor || "N/A"}</TableCell>
                                    <TableCell align="center" className="flex gap-1 justify-center">
                                      <div className="flex items-center gap-1 active-btn">
                                        <div
                                          className="btn btn-indigo btn-icon"
                                          onClick={() => handleEdit(row)}
                                        >
                                          <Edit color="primary" fontSize="small" />
                                        </div>
                                        <div
                                         className="btn btn-denger btn-icon"
                                          onClick={() => deleteClass(row)}
                                        >
                                          <Delete color="primary" fontSize="small" />
                                        </div>
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                ))
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
            
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modern Drawer-based Add/Edit Form - Same as Todo Page */}
      <Drawer
        anchor="right"
        open={settings}
        onClose={closeModal}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 400, md: 450 },
            maxWidth: '90vw',
            fontFamily: '"Saira", sans-serif'
          }
        }}
      >
        <form onSubmit={handleSubmit} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box sx={{
            background: '#ce55b9',
            color: 'white',
            p: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Typography variant="h5" sx={{ fontWeight: 600, fontFamily: '"Saira", sans-serif' }}>
              {isEditing ? 'Edit Class' : 'Add New Class'}
            </Typography>
            <IconButton
              onClick={closeModal}
              disabled={submitting}
              sx={{
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              <Close />
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{
            flex: 1,
            p: 3,
            overflow: 'auto',
            background: '#ffffff'
          }}>
            
            {/* Course Name (only when adding) */}
            {!isEditing && (
              <TextField
                fullWidth
                label="Course Name"
                value={courseName}
                onChange={(e) => setCourseName(e.target.value)}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    fontFamily: '"Saira", sans-serif',
                    '&.Mui-focused': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#ce55b9',
                      }
                    }
                  },
                  '& .MuiInputLabel-root': {
                    fontFamily: '"Saira", sans-serif',
                    '&.Mui-focused': {
                      color: '#ce55b9'
                    }
                  }
                }}
                disabled={submitting}
                placeholder="Enter course name"
              />
            )}

            {/* Class Name */}
            <TextField
              fullWidth
              label="Class Name"
              value={className}
              onChange={(e) => setClassName(e.target.value)}
              required
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontFamily: '"Saira", sans-serif',
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#ce55b9',
                    }
                  }
                },
                '& .MuiInputLabel-root': {
                  fontFamily: '"Saira", sans-serif',
                  '&.Mui-focused': {
                    color: '#ce55b9'
                  }
                }
              }}
              disabled={submitting}
              placeholder="Enter Class Name"
            />

            {/* Professor */}
            <TextField
              fullWidth
              label="Professor"
              value={professor}
              onChange={(e) => setProfessor(e.target.value)}
              required
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontFamily: '"Saira", sans-serif',
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#ce55b9',
                    }
                  }
                },
                '& .MuiInputLabel-root': {
                  fontFamily: '"Saira", sans-serif',
                  '&.Mui-focused': {
                    color: '#ce55b9'
                  }
                }
              }}
              disabled={submitting}
              placeholder="Enter Professor Name"
            />

            {/* Date */}
            <TextField
              fullWidth
              label="Date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              required
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontFamily: '"Saira", sans-serif',
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#ce55b9',
                    }
                  }
                },
                '& .MuiInputLabel-root': {
                  fontFamily: '"Saira", sans-serif',
                  '&.Mui-focused': {
                    color: '#ce55b9'
                  }
                }
              }}
              disabled={submitting}
              helperText={!isEditing ? "Pre-filled with current date" : ""}
            />

            {/* Time Fields */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Start Time"
                  type="time"
                  value={time}
                  onChange={(e) => {
                    const newStartTime = e.target.value;
                    setTime(newStartTime);
                    // Automatically set end time to 30 minutes after start time
                    if (newStartTime) {
                      setEndTime(addMinutesToTime(newStartTime, 30));
                    }
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  required
                  sx={{
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      fontFamily: '"Saira", sans-serif',
                      '&.Mui-focused': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#ce55b9',
                        }
                      }
                    },
                    '& .MuiInputLabel-root': {
                      fontFamily: '"Saira", sans-serif',
                      '&.Mui-focused': {
                        color: '#ce55b9'
                      }
                    }
                  }}
                  disabled={submitting}
                  helperText={!isEditing ? "Pre-filled with current time" : ""}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="End Time"
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      fontFamily: '"Saira", sans-serif',
                      '&.Mui-focused': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#ce55b9',
                        }
                      }
                    },
                    '& .MuiInputLabel-root': {
                      fontFamily: '"Saira", sans-serif',
                      '&.Mui-focused': {
                        color: '#ce55b9'
                      }
                    }
                  }}
                  disabled={submitting}
                  helperText={!isEditing ? "Auto-set to 30 minutes after start time" : ""}
                />
              </Grid>
            </Grid>

            {/* Location */}
            <TextField
              fullWidth
              label="Location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              required
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontFamily: '"Saira", sans-serif',
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#ce55b9',
                    }
                  }
                },
                '& .MuiInputLabel-root': {
                  fontFamily: '"Saira", sans-serif',
                  '&.Mui-focused': {
                    color: '#ce55b9'
                  }
                }
              }}
              disabled={submitting}
              placeholder="Enter Location"
            />
          </Box>

          {/* Footer Actions */}
          <Box sx={{
            p: 3,
            borderTop: '1px solid rgba(0,0,0,0.1)',
            background: 'white',
            display: 'flex',
            gap: 2,
            justifyContent: 'flex-end'
          }}>
            <Button
              onClick={closeModal}
              disabled={submitting}
              variant="outlined"
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontFamily: '"Saira", sans-serif',
                px: 3,
                py: 1.5,
                minWidth: 100
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting}
              sx={{
                background: '#ce55b9',
                borderRadius: 2,
                textTransform: 'none',
                fontFamily: '"Saira", sans-serif',
                px: 3,
                py: 1.5,
                minWidth: 140,
                '&:hover': {
                  background: '#b8479a',
                },
                '&:disabled': {
                  background: '#bdbdbd',
                  color: 'white'
                }
              }}
            >
              {submitting ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} sx={{ color: 'white' }} />
                  <Typography sx={{ fontFamily: '"Saira", sans-serif' }}>
                    {isEditing ? 'Updating...' : 'Creating...'}
                  </Typography>
                </Box>
              ) : (
                <Typography sx={{ fontFamily: '"Saira", sans-serif' }}>
                  {isEditing ? 'Update Class' : 'Create Class'}
                </Typography>
              )}
            </Button>
          </Box>
        </form>
      </Drawer>
    </Grid>
  );
};

export default ClassSchedulesTab;